
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../../components/docs/ContentSwitcher';
import SampleCode from '../../../../components/docs/plaidoms/SampleCode';

export const metadata = {
  toc: true,
  layout: 'guide',
  maxTocLevel: 4,
  description: 'Use Stripe with Plaid Auth to send and receive payments',
  preloadImages: [{
    src: '/assets/img/products/plaid-link-connect-account-flow.png',
    type: 'image/webp'
  }]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Stripe to your app' subtitle={metadata.description} mdxType="Header" />
    <Image src='/assets/img/products/plaid-link-connect-account-flow.png' width='624' alt='Plaid + Stripe logo image' mdxType="Image" />
    <br />
    <p>{`Plaid and Stripe have partnered to offer frictionless money transfers without the need to ever handle an account or routing number. Use `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Plaid Link`}</a>{` to instantly authenticate your customer's account and generate a Stripe `}<a parentName="p" {...{
        "href": "https://stripe.com/docs/api/tokens/create_bank_account"
      }}>{`bank account token`}</a>{` so that you can accept ACH payments via Stripe. `}</p>
    <p>{`This guide is designed for those who already have developer accounts with both Stripe and Plaid. If that's not you, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`Sign up for a Plaid Dashboard account`}</a>{`, then head over to the `}<a parentName="p" {...{
        "href": "https://dashboard.stripe.com/register"
      }}>{`Stripe docs`}</a>{` to get a Stripe account.`}</p>
    <Callout mdxType="Callout">
      <p>{`This guide describes integrating Plaid with the `}<a parentName="p" {...{
          "href": "https://docs.stripe.com/payments/payment-intents"
        }}>{`Stripe Payment Intents API`}</a>{`, which is the modern way to send and receive ACH payments via Stripe. `}</p>
      <p>{`Existing customers who have integrations using the older Stripe Charges and Sources API should contact their Plaid account manager with any questions. For more details on the deprecation of the Charges and Sources API, see the `}<a parentName="p" {...{
          "href": "/docs/changelog/#february-26-2026"
        }}>{`changelog`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "getting-started"
    }}>{`Getting Started`}</h4>
    <p>{`You'll first want to familiarize yourself with `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Plaid Link`}</a>{`, a drop-in client-side integration for the Plaid API that handles input validation, error handling, and multi-factor authentication.`}</p>
    <p>{`Your customers will use Link to authenticate with their financial institution and select the depository account they wish to use for ACH transactions. From there, you'll receive a Plaid `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, allowing you to leverage real-time balance checks and transaction data, and a Stripe `}<inlineCode parentName="p">{`bank_account_token`}</inlineCode>{`, which allows you to move money via Stripe's ACH API without ever handling an account or routing number.`}</p>
    <h4 {...{
      "id": "instructions"
    }}>{`Instructions`}</h4>
    <h5 {...{
      "id": "set-up-your-plaid-and-stripe-accounts"
    }}>{`Set up your Plaid and Stripe accounts`}</h5>
    <p>{`You'll need accounts at both Plaid and Stripe in order to use the Plaid Link + Stripe integration. You'll also need to connect your Plaid and Stripe accounts so that Plaid can facilitate the creation of bank account tokens on your behalf.`}</p>
    <p>{`First, `}<a parentName="p" {...{
        "href": "https://dashboard.stripe.com/register"
      }}>{`sign up for a Stripe account`}</a>{` if you do not already have one, and then verify that it is enabled for ACH access. To verify that your Stripe account is ACH enabled, head to the Settings → Payment Methods section of the Stripe Dashboard and make sure that ACH Direct Debit is marked as enabled. If it is not, click the button to enable it. `}</p>
    <p>{`If you do not have a Plaid account, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup/stripe"
      }}>{`create one`}</a>{`.`}</p>
    <p>{`To verify that your Plaid account is enabled for the Stripe integration, go to the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/integrations"
      }}>{`Integrations`}</a>{` section of the account dashboard. If you see:`}</p>
    <Image src='/assets/img/products/dashboard-stripe-account-unlinked.png' width='624' alt='Unlinked Stripe account' mdxType="Image" />
    <p>{`your Plaid account is enabled for the integration but you have not connected your Stripe account.`}</p>
    <p>{`Click the 'Connect' button to connect your Plaid and Stripe accounts. This step is required so that Plaid can facilitate the creation of Stripe bank account tokens on your behalf.`}</p>
    <p>{`Once your Stripe account is connected, you'll see:`}</p>
    <Image src='/assets/img/products/dashboard-stripe-account-linked.png' width='624' alt='linked Stripe account' mdxType="Image" />
    <p>{`Your Plaid account is now set up for the integration!`}</p>
    <h6 {...{
      "id": "contact-stripe-to-enable-the-payment-intents-api-for-plaid"
    }}>{`Contact Stripe to enable the Payment Intents API for Plaid`}</h6>
    <p>{`To use Plaid with the Stripe Payment Intents API, you will need to be manually enabled by Stripe.`}</p>
    <p>{`To request access, visit the `}<a parentName="p" {...{
        "href": "https://stripe.partners/directory/plaid"
      }}>{`Stripe partner page`}</a>{`, click 'Get Started', and submit the intake form. Stripe will follow up with next steps.`}</p>
    <h5 {...{
      "id": "complete-your-plaid-application-profile-and-company-profile"
    }}>{`Complete your Plaid application profile and company profile`}</h5>
    <p>{`After connecting your Stripe and Plaid accounts, you'll need to complete your Plaid `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` in the Dashboard, which involves filling out basic information about your app, such as your company name and website. This step helps your end-users learn more about how your product uses their bank information and is also required for connecting to some banks.`}</p>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <p>{`In order to integrate with Plaid Link, you will first need to create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`. A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`
is a short-lived, one-time use token that is used to authenticate your app with Link. To create one,
make a `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request with your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and a few other
required parameters from your app server. For a full list of parameters, see the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` reference.`}</p>
    <p>{`To see your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, visit the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`Plaid Dashboard`}</a>{`.`}</p>
    <SampleCode.CreateLinkToken product='auth' />
    <h5 {...{
      "id": "integrate-with-plaid-link"
    }}>{`Integrate with Plaid Link`}</h5>
    <p>{`Once you have a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`, all it takes is a few lines of client-side JavaScript to launch Link.
Then, in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback, you can call a simple server-side handler to exchange the Link `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`
for a Plaid `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and a Stripe bank account token.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #Integrate Link",
        "switcher": "false",
        "#Integrate": true,
        "Link": true
      }}>{`<button id="linkButton">Open Link - Institution Select</button>
<script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
<script>
  let linkHandler;
  (async function(){
    linkHandler = Plaid.create({
      // Make a request to your server to fetch a new link_token.
      token: (await $.post('/create_link_token')).link_token,
      onLoad: function() {
          // The Link module finished loading.
      },
      onSuccess: function(public_token, metadata) {
        // The onSuccess function is called when the user has
        // successfully authenticated and selected an account to
        // use.
        //
        // When called, you will send the public_token
        // and the data about which account to use
        // to your backend app server. In this example,
        // we assume "Account Select" is set to
        // "Single select".
        //
        // sendDataToBackendServer({
        //   public_token: public_token,
        //   account_id: metadata.accounts[0].id
        // });
      },
      onExit: function(err, metadata) {
        // The user exited the Link flow.
        if (err != null) {
            // The user encountered a Plaid API error
            // prior to exiting.
        }
        // metadata contains information about the institution
        // that the user selected and the most recent
        // API request IDs.
        // Storing this information can be helpful for support.
      },
    });
  })();

  // Trigger the authentication view
  document.getElementById('linkButton').onclick = function() {
    linkHandler.open();
  };
</script>
`}</code></pre>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/docs/link/web#create"
      }}>{`Link parameter reference`}</a>{` for complete documentation on possible configurations.`}</p>
    <p><inlineCode parentName="p">{`Plaid.create`}</inlineCode>{` accepts one argument, a configuration `}<inlineCode parentName="p">{`Object`}</inlineCode>{`, and returns an `}<inlineCode parentName="p">{`Object`}</inlineCode>{` with four functions, `}<a parentName="p" {...{
        "href": "/docs/link/web#open"
      }}><inlineCode parentName="a">{`open`}</inlineCode></a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/web#exit"
      }}><inlineCode parentName="a">{`exit`}</inlineCode></a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/web#submit"
      }}><inlineCode parentName="a">{`submit`}</inlineCode></a>{`, and `}<a parentName="p" {...{
        "href": "/docs/link/web#destroy"
      }}><inlineCode parentName="a">{`destroy`}</inlineCode></a>{`. Calling `}<inlineCode parentName="p">{`open`}</inlineCode>{` will display the "Institution Select" view, calling `}<inlineCode parentName="p">{`exit`}</inlineCode>{` will close Link, and calling `}<inlineCode parentName="p">{`destroy`}</inlineCode>{` will clean up the iframe.`}</p>
    <h5 {...{
      "id": "write-server-side-handler"
    }}>{`Write server-side handler`}</h5>
    <p>{`The Link module handles the entire onboarding flow securely and quickly, but does not actually retrieve account data for a user. Instead, the Link module returns a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` and an `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array, which is a property on the `}<inlineCode parentName="p">{`metadata`}</inlineCode>{` object, via the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback. Exchange this `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for a Plaid `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` using the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` API endpoint.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array will contain information about bank accounts associated with the credentials entered by the user, and may contain multiple accounts if the user has more than one bank account at the institution. In order to avoid any confusion about which account your user wishes to use with Stripe, it is recommended to set `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link/account-select"
      }}>{`Account Select`}</a>{` to "Single select" in the Plaid Dashboard. When this setting is selected, the `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array will always contain exactly one element.`}</p>
    <p>{`Once you have identified the account you will use, you will call `}<inlineCode parentName="p">{`/processor/stripe/bank_account_token/create`}</inlineCode>{`, sending Plaid the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` property of the account along with the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` you obtained from `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{`. Plaid will create and return a Stripe `}<a parentName="p" {...{
        "href": "https://stripe.com/docs/api/tokens/create_bank_account"
      }}>{`bank account token`}</a>{` for this account, which can then be used to move money via Stripe's ACH API. The bank account token will be linked to the Stripe account you linked in your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/"
      }}>{`Plaid Dashboard`}</a>{`.`}</p>
    <p>{`Note that the Stripe bank account token is a one-time use token. To store bank account information for later use, you can use a Stripe `}<a parentName="p" {...{
        "href": "https://stripe.com/docs/api/customers/object"
      }}>{`customer object`}</a>{` and create an associated `}<a parentName="p" {...{
        "href": "https://stripe.com/docs/api/customer_bank_accounts/create"
      }}>{`bank account`}</a>{` from the token, or you can use a Stripe `}<a parentName="p" {...{
        "href": "https://stripe.com/docs/api/accounts"
      }}>{`Custom account`}</a>{` and create an associated `}<a parentName="p" {...{
        "href": "https://stripe.com/docs/api/external_account_bank_accounts/create"
      }}>{`external bank account`}</a>{` from the token. This bank account information should work indefinitely, unless the user's bank account information changes or they revoke Plaid's permissions to access their account (when using certain financial institutions). The Plaid `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, on the other hand, does not expire and should be persisted securely.`}</p>
    <p>{`Stripe bank account information cannot be modified once the bank account token has been created. If you ever need to change the bank account details used by Stripe for a specific customer, you should repeat this process from the beginning, including creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`, sending the customer through the Link flow, generating a new bank account token, and creating a new customer object or Custom account.`}</p>
    <MDXCodeBlock title='/processor/stripe/bank_account_token/create' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`// Change sandbox to production when you're ready to go live!
const {
  Configuration,
  PlaidApi,
  PlaidEnvironments,
  ProcessorStripeBankAccountTokenCreateRequest,
} = require('plaid');
const configuration = new Configuration({
  basePath: PlaidEnvironments[process.env.PLAID_ENV],
  baseOptions: {
    headers: {
      'PLAID-CLIENT-ID': process.env.PLAID_CLIENT_ID,
      'PLAID-SECRET': process.env.PLAID_SECRET,
      'Plaid-Version': '2020-09-14',
    },
  },
});

const plaidClient = new PlaidApi(configuration);

try {
  // Exchange the public_token from Plaid Link for an access token.
  const tokenResponse = await plaidClient.itemPublicTokenExchange({
    public_token: publicToken,
  });
  const accessToken = tokenResponse.data.access_token;

  // Generate a bank account token
  const request: ProcessorStripeBankAccountTokenCreateRequest = {
    access_token: accessToken,
    account_id: accountID,
  };
  const stripeTokenResponse = await plaidClient.processorStripeBankAccountTokenCreate(
    request,
  );
  const bankAccountToken = stripeTokenResponse.data.stripe_bank_account_token;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`# Exchange token
curl -X POST https://sandbox.plaid.com/item/public_token/exchange \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "public_token": "\${PUBLIC_TOKEN}"
}'

# Create bank account token
curl -X POST https://sandbox.plaid.com/processor/stripe/bank_account_token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": "\${ACCESS_TOKEN}",
  "account_id": "\${ACCOUNT_ID}"
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

# Change sandbox to production when you're ready to go live!
configuration = Plaid::Configuration.new
configuration.server_index = Plaid::Configuration::Environment[ENV['PLAID_ENV'] || 'sandbox']
configuration.api_key['PLAID-CLIENT-ID'] = ENV['PLAID_CLIENT_ID']
configuration.api_key['PLAID-SECRET'] = ENV['PLAID_SECRET']
configuration.api_key['Plaid-Version'] = '2020-09-14'

api_client = Plaid::ApiClient.new(
  configuration
)

client = Plaid::PlaidApi.new(api_client)

item_public_token_exchange_request = Plaid::ItemPublicTokenExchangeRequest.new
item_public_token_exchange_request.public_token = public_token
exchange_token_response =
client.item_public_token_exchange(
  item_public_token_exchange_request
)
access_token = exchange_token_response.access_token

processor_token_create_request = Plaid::ProcessorStripeBankAccountTokenCreateRequest.new
processor_token_create_request.access_token = access_token
processor_token_create_request.account_id = account_id

stripe_response = client.processor_stripe_bank_account_token_create(processor_token_create_request)
bank_account_token = stripe_response.stripe_bank_account_token
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.ItemPublicTokenExchangeRequest;
import com.plaid.client.model.ItemPublicTokenExchangeResponse;
import com.plaid.client.model.ProcessorStripeBankAccountTokenCreateRequest;
import com.plaid.client.model.ProcessorStripeBankAccountTokenCreateResponse;

// Change sandbox to production when you're ready to go live!
HashMap<String, String> apiKeys = new HashMap<String, String>();
apiKeys.put("clientId", plaidClientId);
apiKeys.put("secret", plaidSecret);
apiKeys.put("plaidVersion", "2020-09-14");
apiClient = new ApiClient(apiKeys);
apiClient.setPlaidAdapter(ApiClient.Sandbox);

plaidClient = apiClient.createService(PlaidApi.class);

// Exchange the public token from Plaid Link for an access token.
ItemPublicTokenExchangeRequest request = new ItemPublicTokenExchangeRequest()
  .publicToken(publicToken);

Response<ItemPublicTokenExchangeResponse> exchangeResponse = plaidClient
  .itemPublicTokenExchange(request)
  .execute();

if (exchangeResponse.isSuccessful()) {
  String accessToken = exchangeResponse.body().getAccessToken();

  ProcessorStripeBankAccountTokenCreateRequest processorTokenRequest = new ProcessorStripeBankAccountTokenCreateRequest()
    .accessToken(accessToken)
    .accountId("FooBarAccountId");

  Response<ProcessorStripeBankAccountTokenCreateResponse> stripeResponse = plaidClient
    .processorStripeBankAccountTokenCreate(processorTokenRequest)
    .execute();

  if (stripeResponse.isSuccessful()) {
    String bankAccountToken = stripeResponse.body().getStripeBankAccountToken();
  }
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import plaid
from plaid.api import plaid_api
from plaid.model.item_public_token_exchange_request import ItemPublicTokenExchangeRequest
from plaid.model.processor_stripe_bank_account_token_create_request import ProcessorStripeBankAccountTokenCreateRequest

# Change sandbox to production when you're ready to go live!
configuration = plaid.Configuration(
    host=plaid.Environment.Sandbox,
    api_key={
        'clientId': PLAID_CLIENT_ID,
        'secret': PLAID_SECRET,
        'plaidVersion': '2020-09-14'
    }
)
api_client = plaid.ApiClient(configuration)
client = plaid_api.PlaidApi(api_client)

exchange_request = ItemPublicTokenExchangeRequest(public_token=public_token)
exchange_token_response = client.item_public_token_exchange(exchange_request)
access_token = exchange_token_response['access_token']

request = ProcessorStripeBankAccountTokenCreateRequest(
    access_token=access_token,
    account_id=account_id,
)
stripe_response = client.processor_stripe_bank_account_token_create(request)
bank_account_token = stripe_response['stripe_bank_account_token']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
  "context"
  "os"

  plaid "github.com/plaid/plaid-go/v48/plaid"
)

// create the client
configuration := plaid.NewConfiguration()
configuration.AddDefaultHeader("PLAID-CLIENT-ID", os.Getenv("CLIENT_ID"))
configuration.AddDefaultHeader("PLAID-SECRET", os.Getenv("SECRET"))
configuration.UseEnvironment(plaid.Sandbox)
client := plaid.NewAPIClient(configuration)
ctx := context.Background()

// exchange the public_token for an access_token
exchangePublicTokenResp, _, err := client.PlaidApi.ItemPublicTokenExchange(ctx).ItemPublicTokenExchangeRequest(
  *plaid.NewItemPublicTokenExchangeRequest("PUBLIC_TOKEN"),
).Execute()
accessToken := exchangePublicTokenResp.GetAccessToken()

// get the account id
accountsResp, _, err := client.PlaidApi.AccountsGet(ctx).AccountsGetRequest(plaid.AccountsGetRequest{
  AccessToken: accessToken,
}).Execute()
accountID := accountsResp.GetAccounts()[0].GetAccountId()

// create a stripe token for the specific account id
request := plaid.NewProcessorStripeBankAccountTokenCreateRequest(accessToken, accountID)
stripeTokenResp, _, err := client.PlaidApi.ProcessorStripeBankAccountTokenCreate(ctx).ProcessorStripeBankAccountTokenCreateRequest(
  *request,
).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"
    "os"

    plaid "github.com/plaid/plaid-go/v49"
    plaidclient "github.com/plaid/plaid-go/v49/client"
    "github.com/plaid/plaid-go/v49/option"
)

// create the client
client := plaidclient.NewClient(
    option.WithClientID(plaid.String(os.Getenv("CLIENT_ID"))),
    option.WithSecret(os.Getenv("SECRET")),
    option.WithBaseURL(plaid.Environments.Sandbox),
)

// exchange the public_token for an access_token
exchangePublicTokenResp, err := client.Item.ExchangePublicToken(ctx, &plaid.ItemPublicTokenExchangeRequest{
    PublicToken: "PUBLIC_TOKEN",
})
accessToken := exchangePublicTokenResp.AccessToken

// get the account id
accountsResp, err := client.Accounts.Get(ctx, &plaid.AccountsGetRequest{
    AccessToken: accessToken,
})
accountID := accountsResp.Accounts[0].AccountID

// create a stripe token for the specific account id
request := &plaid.ProcessorStripeBankAccountTokenCreateRequest{
    AccessToken: accessToken,
    AccountID:   accountID,
}
stripeTokenResp, err := client.Processor.CreateStripeBankAccountToken(ctx, request)
`}</code></pre>
    </MDXCodeBlock>
    <p>{`For a valid request, the API will return a JSON response similar to:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample response",
        "#Sample": true,
        "response": true
      }}>{`{
  "stripe_bank_account_token": "btok_5oEetfLzPklE1fwJZ7SG",
  "request_id": "[Unique request ID]"
}
`}</code></pre>
    <p>{`For possible error codes, see the full listing of Plaid `}<a parentName="p" {...{
        "href": "/docs/errors"
      }}>{`error codes`}</a>{`.`}</p>
    <p>{`Note: The `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` parameter is required if you wish to receive a Stripe bank account token.`}</p>
    <h5 {...{
      "id": "test-with-sandbox-credentials"
    }}>{`Test with Sandbox credentials`}</h5>
    <p>{`You can create Stripe bank account tokens in both API environments:`}</p>
    <ul>
      <li parentName="ul">{`Sandbox (`}<a parentName="li" {...{
          "href": "https://sandbox.plaid.com"
        }}>{`https://sandbox.plaid.com`}</a>{`): test simulated users with Stripe's "test mode" API`}</li>
      <li parentName="ul">{`Production (`}<a parentName="li" {...{
          "href": "https://production.plaid.com"
        }}>{`https://production.plaid.com`}</a>{`): production environment for when you're ready to go live`}</li>
    </ul>
    <p>{`Plaid's Sandbox API environment is compatible with Stripe's "test mode" API. To test the integration in Sandbox mode, simply use the Plaid `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials"
      }}>{`Sandbox credentials`}</a>{` when launching Link with a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` created in the Sandbox environment. The Stripe bank account token created in the Sandbox environment will always match the Stripe bank test account with account number 000123456789 and routing number 110000000, and with the Stripe account that is linked in the Plaid Dashboard.`}</p>
    <p>{`Use Stripe's `}<a parentName="p" {...{
        "href": "https://docs.stripe.com/payments/ach-direct-debit"
      }}>{`ACH API`}</a>{` in test mode to create test transfers using the bank account tokens you retrieve from Plaid's Sandbox API environment.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`Plaid's Sandbox is not compatible with the Stripe Sandbox environment. To test your Plaid - Stripe integration, you must use Stripe test mode, rather than Stripe Sandboxes.`}</p>
    </Callout>
    <Callout mdxType="Callout">
      <p>{`When testing in the Sandbox, you have the option to use the `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{` endpoint instead of the end-to-end Link flow to create a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`. When using the `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{`-based flow, the Account Select flow will be bypassed and the `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array will not be populated. On Sandbox, instead of using the `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array, you can call `}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{` and test with any returned account ID associated with an account with the subtype `}<inlineCode parentName="p">{`checking`}</inlineCode>{` or `}<inlineCode parentName="p">{`savings`}</inlineCode>{`.`}</p>
    </Callout>
    <h5 {...{
      "id": "get-ready-for-production"
    }}>{`Get ready for production`}</h5>
    <p>{`Your account is immediately enabled for our Sandbox environment (`}<a parentName="p" {...{
        "href": "https://sandbox.plaid.com"
      }}>{`https://sandbox.plaid.com`}</a>{`), which allows you to test with Sandbox API credentials. To move to Production, request access from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/team/products"
      }}>{`Dashboard`}</a>{`.`}</p>
    <h5 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h5>
    <p>{`Once you're successfully retrieving Stripe `}<inlineCode parentName="p">{`bank_account_token`}</inlineCode>{`s, you're ready to make ACH transactions with Stripe. Head to `}<a parentName="p" {...{
        "href": "https://stripe.com/docs/ach"
      }}>{`Stripe's ACH guide`}</a>{` to get started, and if you have any issues, please reach out to Stripe Support.`}</p>
    <p>{`If you are using the `}<a parentName="p" {...{
        "href": "https://stripe.com/docs/ach-deprecated"
      }}>{`Charges and Sources API`}</a>{`, the Stripe documentation will walk you through the process. `}</p>
    <p>{`If you are using the Payment Intents API, you will first need to create a Stripe `}<a parentName="p" {...{
        "href": "https://stripe.com/docs/api/customers/create"
      }}>{`customer`}</a>{` using the bank account token, then follow the `}<a parentName="p" {...{
        "href": "https://stripe.com/docs/payments/ach-debit/migrations#create-payment-intent"
      }}>{`migration guide`}</a>{` to create a Payment Intent based on the bank account, using the `}<a parentName="p" {...{
        "href": "https://docs.stripe.com/payments/ach-direct-debit/migrating-from-another-processor#manual-bank-account-migration"
      }}>{`manual migration flow`}</a>{`. Note that you will need to contact Stripe to request access to this flow. `}</p>
    <h4 {...{
      "id": "example-code-in-plaid-pattern"
    }}>{`Example code in Plaid Pattern`}</h4>
    <p>{`For a real-life example of an app that integrates a processor partner, see the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding"
      }}>{`Plaid Pattern Account Funding`}</a>{` sample app. Pattern Account Funding is a sample account funding app that creates a processor token to send to your payment partner. (Note that while the processor token creation call in Plaid Pattern's `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern-account-funding/blob/master/server/routes/items.js#L188-L195"
      }}>{`items.js`}</a>{` demonstrates usage of the `}<inlineCode parentName="p">{`/processor/token/create`}</inlineCode>{` endpoint for creating a processor token, Stripe integrations should use the `}<inlineCode parentName="p">{`/processor/stripe/bank_account_token/create`}</inlineCode>{` endpoint instead and create a bank account token.)`}</p>
    <h4 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h4>
    <h5 {...{
      "id": "stripe-bank-account-token-not-returned"
    }}>{`Stripe bank account token not returned`}</h5>
    <p>{`When a `}<inlineCode parentName="p">{`stripe_bank_account_token`}</inlineCode>{` is not returned, a typical cause is that your Stripe and Plaid accounts have not yet been linked. First, be sure that your Stripe account has been `}<a parentName="p" {...{
        "href": "https://docs.stripe.com/payments/ach-direct-debit"
      }}>{`configured to accept ACH transfers`}</a>{`. Then, `}<a parentName="p" {...{
        "href": "#set-up-your-plaid-and-stripe-accounts"
      }}>{`link your Stripe account to your Plaid account`}</a>{` and re-try your request.`}</p>
    <h5 {...{
      "id": "stripeinvalidrequesterror-no-such-token"
    }}>{`Stripe::InvalidRequestError: No such token`}</h5>
    <p>{`The `}<inlineCode parentName="p">{`Stripe::InvalidRequestError: No such token`}</inlineCode>{` error is returned by Stripe when the `}<inlineCode parentName="p">{`bank_account_token`}</inlineCode>{` could not be used with the Stripe API. Make sure you are not mixing Plaid's Sandbox environment with Stripe's production environment and that, if you have more than one Stripe account or more than one Plaid `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, you are using the Stripe account that is linked to the Plaid `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` you are using.`}</p>
    <h4 {...{
      "id": "support-and-questions"
    }}>{`Support and questions`}</h4>
    <p>{`Find answers to many common integration questions and concerns, such as pricing, sandbox and test mode usage in our `}<a parentName="p" {...{
        "href": "/docs"
      }}>{`docs`}</a>{`.`}</p>
    <p>{`If you're still stuck, open a `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new"
      }}>{`support ticket`}</a>{` with information describing the issue that you're experiencing and we'll get back to you as soon as we can.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "getting-started",
        level: 4,
        title: "Getting Started",
      },
{
        id: "instructions",
        level: 4,
        title: "Instructions",
      },
{
        id: "example-code-in-plaid-pattern",
        level: 4,
        title: "Example code in Plaid Pattern",
      },
{
        id: "troubleshooting",
        level: 4,
        title: "Troubleshooting",
      },
{
        id: "support-and-questions",
        level: 4,
        title: "Support and questions",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
